<template>
  <Transition name="ai-panel">
    <section
      v-if="store.isOpen"
      id="kars-ai-panel"
      :aria-label="$gt('KARS-KI Support-Assistent')"
      class="ai-panel"
      data-testid="ai-panel"
      role="dialog"
      @keydown.esc.stop="store.close()"
    >
      <!-- Kopf: Navy wie App-Leiste und Bug-Dialog, KARS-Wortmarke + „KI“. -->
      <header class="ai-panel__head">
        <div class="ai-panel__brand">
          <img alt="KARS" class="ai-panel__logo" :src="logo">
          <span aria-hidden="true" class="ai-panel__ki">KI</span>
        </div>
        <p class="ai-panel__subtitle">
          <span class="ai-panel__dot" :class="{ 'is-busy': store.streaming, 'is-off': !store.enabled }" />
          {{ statusLine }}
        </p>
        <button
          v-if="store.hasConversation"
          :aria-label="$gt('Neue Unterhaltung')"
          class="ai-icon-btn"
          :title="$gt('Neue Unterhaltung')"
          type="button"
          @click="store.newConversation()"
        >
          <SquarePen :size="17" />
        </button>
        <button
          :aria-label="$gt('Schließen')"
          class="ai-icon-btn"
          data-testid="ai-close"
          type="button"
          @click="store.close()"
        >
          <X :size="19" />
        </button>
      </header>

      <!-- Verlauf -->
      <div ref="scroller" class="ai-panel__body" @scroll.passive="onScroll">
        <div v-if="!store.hasConversation" class="ai-panel__hero">
          <h3 class="ai-panel__greeting">
            <span>{{ typedGreeting }}</span><span v-if="greetingTyping" aria-hidden="true" class="ai-panel__greeting-caret" />
          </h3>
          <p class="ai-panel__lead">{{ $gt('Ich erkläre Ihnen jede Funktion von KARS und bringe Sie mit einem Klick an die richtige Stelle.') }}</p>

          <AssistantInsights
            :insights="store.insights"
            @ask="askAboutInsight"
            @dismiss="store.dismissInsight"
            @open="openRoute"
          />

          <div v-if="store.starterSuggestions.length > 0" class="ai-panel__starters">
            <p class="ai-panel__section-title">{{ $gt('Häufige Fragen') }}</p>
            <div class="ai-panel__chips">
              <button
                v-for="s in store.starterSuggestions"
                :key="s"
                class="ai-chip"
                type="button"
                @click="sendSuggestion(s)"
              >
                {{ s }}
              </button>
            </div>
          </div>

          <p v-if="!store.enabled && store.contextLoaded" class="ai-panel__notice">
            <Info aria-hidden="true" :size="14" />
            {{ $gt('Der Chat ist in dieser Umgebung nicht aktiviert. Die Anleitungen finden Sie im Hilfe-Center.') }}
            <button class="ai-link" type="button" @click="openRoute({ name: 'PageHelp' })">
              {{ $gt('Hilfe-Center') }}
              <ArrowRight aria-hidden="true" :size="13" />
            </button>
          </p>
        </div>

        <template v-else>
          <AssistantMessage
            v-for="message in store.messages"
            :key="message.key"
            :message="message"
            @navigate="openRoute"
            @rate="store.rate"
            @report="reportBug"
          />
          <AssistantThinking
            v-if="showThinking"
            :step="store.step"
            :step-text="store.stepText"
          />
          <div v-if="store.suggestions.length > 0 && !store.streaming" class="ai-panel__chips ai-panel__chips--follow">
            <button
              v-for="s in store.suggestions"
              :key="s"
              class="ai-chip ai-rise"
              type="button"
              @click="sendSuggestion(s)"
            >
              {{ s }}
            </button>
          </div>
          <div aria-hidden="true" class="ai-panel__spacer" :style="{ height: `${spacerHeight}px` }" />
        </template>
      </div>

      <Transition name="ai-fade">
        <button
          v-if="!atBottom"
          :aria-label="$gt('Nach unten')"
          class="ai-panel__to-bottom"
          type="button"
          @click="scrollToBottom(true)"
        >
          <ArrowDown :size="16" />
        </button>
      </Transition>

      <!-- Eingabe und Schnellzugriff auf die Fehlermeldung -->
      <div class="ai-panel__foot">
        <AssistantComposer
          ref="composer"
          :disabled="!store.enabled"
          :prefill="store.pendingQuestion"
          :streaming="store.streaming"
          @consumed="store.pendingQuestion = null"
          @send="send"
          @stop="store.stop()"
        />
        <div class="ai-panel__actions">
          <button class="ai-text-btn" data-testid="ai-report-bug" type="button" @click="reportBug">
            <Bug aria-hidden="true" :size="14" />
            {{ $gt('Fehler melden') }}
          </button>
          <span aria-hidden="true" class="ai-panel__hint"><kbd>↵</kbd> {{ $gt('Senden') }} · <kbd>{{ modKey }}J</kbd></span>
        </div>
      </div>
    </section>
  </Transition>
</template>

<script setup lang="ts">
  import type { AssistantInsight, AssistantRoute } from '@/services/assistantApi'
  import { ArrowDown, ArrowRight, Bug, Info, SquarePen, X } from 'lucide-vue-next'
  import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
  import { type RouteLocationRaw, useRoute, useRouter } from 'vue-router'
  import logo from '@/assets/images/logo_white.png'
  import { gt } from '@/i18n/gt'
  import { useAssistantStore } from '@/stores/assistantStore'
  import { useDisplayStore } from '@/stores/displayStore'
  import { useUiStore } from '@/stores/uiStore'
  import AssistantComposer from './AssistantComposer.vue'
  import AssistantInsights from './AssistantInsights.vue'
  import AssistantMessage from './AssistantMessage.vue'
  import AssistantThinking from './AssistantThinking.vue'
  import { prefersReducedMotion } from './useTypewriter'

  const store = useAssistantStore()
  const uiStore = useUiStore()
  const route = useRoute()
  const router = useRouter()
  const display = useDisplayStore()

  const scroller = ref<HTMLElement | null>(null)
  const composer = ref<InstanceType<typeof AssistantComposer> | null>(null)
  const atBottom = ref(true)
  /** Höhe des Abstandhalters unter dem Verlauf (siehe pinLatestQuestion). */
  const spacerHeight = ref(0)
  const modKey = computed(() => (typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Strg+'))

  // ── Gruß ────────────────────────────────────────────────────────────────
  const greeting = computed(() => {
    const hour = new Date().getHours()
    const name = store.firstName ? `, ${store.firstName}` : ''
    if (hour < 11) {
      return `${gt('Guten Morgen')}${name}.`
    }
    if (hour < 18) {
      return `${gt('Hallo')}${name}.`
    }
    return `${gt('Guten Abend')}${name}.`
  })
  const typedGreeting = ref('')
  const greetingTyping = ref(false)
  let greetingTimer = 0

  function typeGreeting () {
    window.clearInterval(greetingTimer)
    const full = greeting.value
    if (prefersReducedMotion()) {
      typedGreeting.value = full
      greetingTyping.value = false
      return
    }
    typedGreeting.value = ''
    greetingTyping.value = true
    let i = 0
    greetingTimer = window.setInterval(() => {
      i += 1
      typedGreeting.value = full.slice(0, i)
      if (i >= full.length) {
        window.clearInterval(greetingTimer)
        window.setTimeout(() => {
          greetingTyping.value = false
        }, 900)
      }
    }, Math.max(18, Math.min(45, 420 / full.length)))
  }

  const statusLine = computed(() => {
    if (!store.enabled && store.contextLoaded) {
      return gt('Nur Hinweise verfügbar')
    }
    if (store.streaming) {
      return gt('Recherchiert und antwortet …')
    }
    return gt('Ihr Assistent für KARS')
  })

  const showThinking = computed(() => {
    const last = store.lastAssistantMessage
    return store.streaming && !!last && last.streaming && last.content.length === 0
  })

  // ── Scrollen ────────────────────────────────────────────────────────────
  function onScroll () {
    const el = scroller.value
    if (!el) {
      return
    }
    atBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight < 48
  }

  function scrollToBottom (smooth = false) {
    const el = scroller.value
    if (!el) {
      return
    }
    el.scrollTo({ top: el.scrollHeight, behavior: smooth && !prefersReducedMotion() ? 'smooth' : 'auto' })
    atBottom.value = true
  }

  function scrollToTop () {
    scroller.value?.scrollTo({ top: 0 })
    atBottom.value = true
  }
  /**
   * Neue Frage: die eigene Nachricht an den oberen Rand holen, damit die
   * Antwort von Anfang an lesbar ist (Feedback André, 05.11.2026). Während
   * des Streamens wird nicht mitgescrollt; der Knopf „Nach unten“ bleibt.
   */
  async function pinLatestQuestion () {
    const el = scroller.value
    if (!el) {
      return
    }
    const questions = el.querySelectorAll<HTMLElement>('[data-testid="ai-message-user"]')
    // eslint-disable-next-line unicorn/prefer-at -- Array#at fehlt in der TS-Lib (vor ES2022)
    const last = questions[questions.length - 1]
    if (!last) {
      return
    }
    const wanted = Math.max(0, last.offsetTop - 8)
    // Ohne Platz unter der Frage ließe sie sich nicht nach oben scrollen;
    // ein Abstandhalter am Ende schafft ihn, solange die Antwort kurz ist.
    const maxScroll = el.scrollHeight - spacerHeight.value - el.clientHeight
    spacerHeight.value = Math.max(0, wanted - maxScroll)
    await nextTick()
    el.scrollTo({ top: wanted, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
    onScroll()
  }

  watch(() => store.messages.length, (count, previous) => {
    // eslint-disable-next-line unicorn/prefer-at -- Array#at fehlt in der TS-Lib (vor ES2022)
    const last = store.messages[store.messages.length - 1]
    if (count > (previous ?? 0) && last?.role === 'assistant' && last.streaming) {
      void nextTick(() => pinLatestQuestion())
    }
  })

  watch(() => store.streaming, () => void nextTick(onScroll))

  watch(() => store.isOpen, open => {
    if (!open) {
      return
    }
    void nextTick(() => {
      typeGreeting()
      if (store.hasConversation) {
        void pinLatestQuestion()
      } else {
        scrollToTop()
      }
      if (!display.isMobile) {
        composer.value?.focus()
      }
    })
  }, { immediate: true })

  // Neue Unterhaltung: Begrüßung erneut tippen und nach oben scrollen.
  watch(() => store.hasConversation, has => {
    if (!has) {
      spacerHeight.value = 0
    }
    if (!has && store.isOpen) {
      typeGreeting()
      void nextTick(scrollToTop)
    }
  })

  watch(() => store.firstName, () => {
    if (store.isOpen && !store.hasConversation) {
      typeGreeting()
    }
  })

  onBeforeUnmount(() => window.clearInterval(greetingTimer))

  // ── Aktionen ────────────────────────────────────────────────────────────
  function currentPage () {
    return { routeName: String(route.name ?? ''), query: { ...route.query } as Record<string, unknown> }
  }

  function currentLocale () {
    try {
      return localStorage.getItem('kars-locale') || 'de'
    } catch {
      return 'de'
    }
  }

  function send (text: string) {
    void store.send(text, currentPage(), currentLocale())
  }

  function sendSuggestion (text: string) {
    send(text)
  }

  function askAboutInsight (insight: AssistantInsight) {
    send(`${insight.title} – ${gt('Was sollte ich dazu wissen?')}`)
  }

  function openRoute (to: RouteLocationRaw | AssistantRoute) {
    const location: RouteLocationRaw = typeof to === 'object' && to && 'name' in to && !('path' in to)
      ? { name: (to as AssistantRoute).name, query: (to as AssistantRoute).query, params: (to as AssistantRoute).params }
      : (to as RouteLocationRaw)
    void router.push(location)
    if (display.isMobile) {
      store.close()
    }
  }

  /**
   * „Fehler melden“ öffnet den bestehenden Bug-Report-Dialog (Screenshot,
   * Systemdaten, GitHub-Issue). Die letzte Frage an KARS-KI wandert als
   * Kontext in die Beschreibung, damit der Support den Zusammenhang kennt.
   */
  function reportBug () {
    let lastQuestion: string | undefined
    for (let i = store.messages.length - 1; i >= 0; i--) {
      const m = store.messages[i]
      if (m?.role === 'user') {
        lastQuestion = m.content
        break
      }
    }
    const description = lastQuestion ? `${gt('Zuletzt an KARS-KI gefragt')}: „${lastQuestion}“\n\n` : ''
    uiStore.openBugReport({ title: '', description })
    // Der Dialog ist modal und liegt unter dem Panel – Panel schließen.
    store.close()
  }
</script>

<style scoped>
  .ai-panel {
    position: fixed;
    right: 24px;
    bottom: calc(80px + var(--app-fab-lift, 0px));
    z-index: var(--ai-z);
    display: grid;
    grid-template-rows: auto 1fr auto;
    width: min(420px, calc(100vw - 32px));
    height: min(680px, calc(100dvh - 104px));
    overflow: hidden;
    border-radius: var(--ai-radius);
    background: var(--ai-surface);
    box-shadow: var(--ai-shadow);
    transform-origin: bottom right;
    isolation: isolate;
  }

  /* ── Kopf ── */
  .ai-panel__head {
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    column-gap: 4px;
    align-items: center;
    padding: 12px 10px 12px 16px;
    background: var(--ai-navy);
    color: #fff;
  }

  .ai-panel__brand {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    grid-row: 1;
  }

  .ai-panel__logo {
    display: block;
    width: auto;
    height: 18px;
  }

  .ai-panel__ki {
    padding: 2px 6px;
    border-radius: 4px;
    background: var(--ai-blue);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    line-height: 14px;
  }

  .ai-panel__subtitle {
    display: flex;
    gap: 6px;
    align-items: center;
    grid-column: 1 / 3;
    grid-row: 2;
    margin: 4px 0 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
  }

  .ai-panel__head .ai-icon-btn {
    grid-row: 1 / 3;
    color: #fff;
  }

  .ai-panel__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #4ade80;
  }

  .ai-panel__dot.is-busy {
    background: #fbbf24;
  }

  .ai-panel__dot.is-off {
    background: #94a3b8;
  }

  /* ── Verlauf ── */
  .ai-panel__body {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 16px 12px;
    overflow-y: auto;
    overscroll-behavior: contain;
    background: var(--ai-surface-2);
  }

  .ai-panel__hero {
    display: grid;
    gap: 16px;
    padding-top: 4px;
  }

  .ai-panel__greeting {
    min-height: 1.2em;
    margin: 0;
    color: var(--ai-ink);
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
  }

  .ai-panel__greeting-caret {
    display: inline-block;
    width: 2px;
    height: 0.9em;
    margin-left: 3px;
    vertical-align: -2px;
    background: var(--ai-blue);
    animation: ai-blink 1s steps(1) infinite;
  }

  .ai-panel__lead {
    margin: -8px 0 0;
    color: var(--ai-muted);
    font-size: 14px;
    line-height: 1.5;
  }

  .ai-panel__section-title {
    margin: 0 0 8px;
    color: var(--ai-muted);
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .ai-panel__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .ai-panel__chips--follow {
    padding-left: 34px;
  }

  .ai-panel__spacer {
    flex: none;
  }

  .ai-panel__notice {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin: 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: var(--ai-blue-soft);
    color: var(--ai-blue-dark);
    font-size: 13px;
  }

  .ai-panel__to-bottom {
    position: absolute;
    right: 20px;
    bottom: 112px;
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 1px solid var(--ai-line);
    border-radius: 50%;
    background: var(--ai-surface);
    color: var(--ai-blue);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    cursor: pointer;
  }

  /* ── Fuß ── */
  .ai-panel__foot {
    display: grid;
    gap: 6px;
    padding: 10px 12px 8px;
    border-top: 1px solid var(--ai-line);
    background: var(--ai-surface);
  }

  .ai-panel__actions {
    display: flex;
    gap: 2px;
    align-items: center;
  }

  .ai-panel__hint {
    margin-left: auto;
    color: var(--ai-faint);
    font-size: 11px;
    white-space: nowrap;
  }

  .ai-panel__hint kbd {
    display: inline-block;
    min-width: 16px;
    padding: 0 4px;
    border: 1px solid var(--ai-line);
    border-radius: 4px;
    background: var(--ai-surface-2);
    color: var(--ai-muted);
    font-family: inherit;
    font-size: 10px;
    line-height: 16px;
    text-align: center;
  }

  /* ── Übergänge ── */
  .ai-panel-enter-active {
    transition: opacity 180ms ease, transform 240ms var(--ai-ease);
  }

  .ai-panel-leave-active {
    transition: opacity 140ms ease, transform 160ms ease;
  }

  .ai-panel-enter-from,
  .ai-panel-leave-to {
    opacity: 0;
    transform: translateY(10px) scale(0.97);
  }

  .ai-fade-enter-active,
  .ai-fade-leave-active {
    transition: opacity 140ms ease;
  }

  .ai-fade-enter-from,
  .ai-fade-leave-to {
    opacity: 0;
  }

  @media (max-width: 599px) {
    .ai-panel {
      inset: 0;
      width: 100vw;
      height: 100dvh;
      border-radius: 0;
    }

    .ai-panel__hint {
      display: none;
    }

    .ai-panel-enter-from,
    .ai-panel-leave-to {
      transform: translateY(16px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-panel__greeting-caret {
      animation: none;
    }

    .ai-panel-enter-active,
    .ai-panel-leave-active {
      transition: opacity 140ms ease;
    }

    .ai-panel-enter-from,
    .ai-panel-leave-to {
      transform: none;
    }
  }
</style>
